import { useEffect, useRef, useState, type ComponentType, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Plus, Layers, BarChart3, History, Award, Settings, LogOut, User, BookOpen, Zap, PlayCircle, Activity } from 'lucide-react';
import { TOKENS, FONTS } from '../ui/tokens';
import { useMotion, EASE_SNAP, DURATION } from '../ui/motion';
import { clearAuthToken } from '../api/client';
import type { LucideProps } from 'lucide-react';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  key: string;
  label: string;
  icon: ComponentType<LucideProps>;
}

/** Drawer width — spec: slide-in from left, 300px wide */
const DRAWER_WIDTH = 300;

/**
 * Focusable selector for the focus trap. Real <button>/<a> elements only.
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Nav sections — mirrors the vanilla drawer (index.html #hamburger-menu):
 * Training / Insights / System groups. Link set per task spec:
 * BottomNav tabs (Home, Techniques, Log, Stats, Settings) + History + Achievements.
 */
const TRAINING: NavItem[] = [
  { key: '/', label: 'Dashboard', icon: Home },
  { key: '/session-log', label: 'Log Training', icon: Plus },
  { key: '/sessions', label: 'History', icon: History },
  { key: '/goals', label: 'Goals', icon: BookOpen },
  { key: '/partners', label: 'Partners', icon: User },
];

const INSIGHTS: NavItem[] = [
  { key: '/stats', label: 'Stats', icon: BarChart3 },
  { key: '/pulse', label: 'Pulse', icon: Zap },
  { key: '/cloze', label: 'Review', icon: BookOpen },
  { key: '/techniques', label: 'Techniques', icon: Layers },
  { key: '/instructionals', label: 'Instructionals', icon: PlayCircle },
  { key: '/health', label: 'Health', icon: Activity },
];

const SYSTEM: NavItem[] = [
  { key: '/achievements', label: 'Achievements', icon: Award },
  { key: '/settings', label: 'Settings', icon: Settings },
];

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '12px 14px 4px',
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.15em',
        color: TOKENS.textTertiary,
        fontFamily: FONTS.mono,
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  );
}

interface NavLinkProps {
  item: NavItem;
  onNavigate: (key: string) => void;
}

function NavLink({ item, onNavigate }: NavLinkProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.key)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '10px 14px',
        borderRadius: 8,
        border: 'none',
        background: hovered ? TOKENS.field : 'none',
        cursor: 'pointer',
        color: TOKENS.textPrimary,
        fontFamily: FONTS.body,
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'left',
        transition: 'background-color 120ms ease',
      }}
    >
      <item.icon size={18} color={TOKENS.amber} strokeWidth={2} />
      <span>{item.label}</span>
    </button>
  );
}

function DrawerSignOutButton({ onSignOut }: { onSignOut: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onSignOut}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        width: '100%', padding: '10px 14px', borderRadius: 8,
        border: 'none', background: hovered ? TOKENS.field : 'none',
        cursor: 'pointer', color: TOKENS.danger, fontFamily: FONTS.body,
        fontSize: 14, fontWeight: 500, textAlign: 'left',
        transition: 'background-color 120ms ease',
      }}
    >
      <LogOut size={18} color={TOKENS.danger} strokeWidth={2} />
      <span>Sign Out</span>
    </button>
  );
}

/**
 * Drawer — slide-in navigation menu for the app shell.
 *
 * MOTION: the panel uses the Accordion expand/collapse pattern applied
 * horizontally — `grid-template-columns: 0fr → 300px` via CSS transition
 * (same 0fr→1fr vocabulary as `Accordion`, no transform, no JS measuring).
 * Reduced motion: duration 0 → instant show/hide, no slide.
 *
 * A11Y: role=dialog + aria-modal, Escape closes, focus trap (Tab cycles
 * first→last, Shift+Tab last→first), focus restored to the trigger on close,
 * `inert` removes the closed panel from tab order and the a11y tree.
 */
export function Drawer({ open, onClose }: DrawerProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const lastActiveRef = useRef<HTMLElement | null>(null);
  const { reduced } = useMotion();
  const dur = reduced ? 0 : DURATION.slow;

  // Focus trap: save trigger, focus first focusable on open; restore on close.
  useEffect(() => {
    if (open) {
      lastActiveRef.current = document.activeElement as HTMLElement | null;
      const panel = panelRef.current;
      const first = panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? panel)?.focus();
    } else if (lastActiveRef.current) {
      lastActiveRef.current.focus();
      lastActiveRef.current = null;
    }
  }, [open]);

  // Lock body scroll while open (vanilla parity: toggleHamburger sets overflow hidden).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape closes — window-level so it works even if focus is momentarily outside.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !panel.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleNavigate = (key: string) => {
    onClose();
    navigate(key);
  };

  const handleSignOut = () => {
    // Vanilla parity with lo(): POST logout, clear local token, redirect home.
    clearAuthToken();
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    onClose();
    navigate('/');
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/* Backdrop — dark semi-transparent, click to close */}
      <div
        role="presentation"
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          opacity: open ? 1 : 0,
          transition: `opacity ${dur}ms ${EASE_SNAP}`,
        }}
      />

      {/* Panel — Accordion expand/collapse pattern (0fr → 300px) */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        inert={!open}
        onKeyDown={handleKeyDown}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: DRAWER_WIDTH,
          transform: open ? 'translateX(0)' : `translateX(-${DRAWER_WIDTH}px)`,
          transition: `transform ${dur}ms ${EASE_SNAP}`,
          overflow: 'hidden',
          background: TOKENS.surface,
          borderRight: `1px solid ${TOKENS.border}`,
          boxShadow: '0 0 40px rgba(0,0,0,0.5)',
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: DRAWER_WIDTH,
            minWidth: 0,
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: TOKENS.surface,
          }}
        >
          {/* Profile section */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderBottom: `1px solid ${TOKENS.border}`,
            }}
          >
            <button
              type="button"
              aria-label="Profile and settings"
              title="Settings"
              onClick={() => handleNavigate('/settings')}
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: TOKENS.amber,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <User size={17} color="#09090b" strokeWidth={2.5} />
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: TOKENS.textPrimary, fontFamily: FONTS.body }}>
                Your Account
              </span>
              <span style={{ fontSize: 11, color: TOKENS.textTertiary, fontFamily: FONTS.body }}>
                Profile &amp; settings
              </span>
            </div>
          </div>

          {/* Nav links */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <SectionLabel>Training</SectionLabel>
            {TRAINING.map((item) => (
              <NavLink key={item.key} item={item} onNavigate={handleNavigate} />
            ))}
            <SectionLabel>Insights</SectionLabel>
            {INSIGHTS.map((item) => (
              <NavLink key={item.key} item={item} onNavigate={handleNavigate} />
            ))}
            <SectionLabel>System</SectionLabel>
            {SYSTEM.map((item) => (
              <NavLink key={item.key} item={item} onNavigate={handleNavigate} />
            ))}
          </div>

          {/* Sign out — pinned to the bottom */}
          <div style={{ borderTop: `1px solid ${TOKENS.border}`, padding: 8 }}>
            <DrawerSignOutButton onSignOut={handleSignOut} />
          </div>
        </div>
      </div>
    </div>
  );
}
